import { useEffect, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';

import { adminApi } from '../adminApi';
import { formatCost, formatDate, formatInt, formatLatency } from '../format';
import { StatusBadge } from '../components/StatusBadge';
import { neoFeedbackReasonLabel } from '../../lib/neoFeedback';
import { GuideLink } from '../evals/GuideLink';

// Neo session transcript (ADR 0119). Opening this view reads a user's PRIVATE
// conversation, so the operator must state a reason first (sent to the server,
// which logs a fail-closed audit row before returning any content). Renders each
// turn with its provenance (model/cost/latency + grounded/truncated/suppressed
// badges), inline tool calls + citations, owner feedback, and the reviewer's
// act-on-it controls (tag, save as eval case). Refusals tied to this session are
// a separate timestamp-ordered panel (they have no seq to interleave on).
// `?turn=<assistant message id>` scrolls to that turn and marks it once the
// transcript is open: the eval case inbox links a candidate's turn here (TOM-140).

/** A stored reason as the owner tapped it, with its code: "Made something up (not_grounded)". */
function feedbackReasonText(code: string): string {
  const label = neoFeedbackReasonLabel(code);
  return label === null ? code : `${label} (${code})`;
}

/**
 * The owner's rating of a turn, the reason they picked and what they expected
 * (TOM-461). Their words are masked like the turns (ADR 0119) until the
 * operator reveals PII.
 */
export function OwnerFeedback({
  feedback,
  revealPii,
}: {
  feedback: { rating: 'up' | 'down'; reason_code: string | null; comment: string | null };
  revealPii: boolean;
}) {
  return (
    <div className="neo-admin-feedback">
      Owner rated {feedback.rating === 'up' ? '👍' : '👎'}
      {feedback.reason_code !== null ? ` · ${feedbackReasonText(feedback.reason_code)}` : ''}
      {feedback.comment !== null ? (
        <>
          {' · '}
          <span className={revealPii ? undefined : 'neo-admin-masked'}>“{feedback.comment}”</span>
        </>
      ) : null}
    </div>
  );
}

interface ToolTraceEntry {
  seq: number;
  tool: string;
  args: string;
  resultSummary: string;
  isError: boolean;
}

interface Citation {
  title: string;
  section: string | null;
  page: number | null;
  url: string | null;
}

interface TurnMeta {
  finish_reason: string | null;
  truncated: boolean;
  errored: boolean;
  manual_attempted: boolean;
  grounded: boolean;
  rag_hit: boolean;
  telemetry_attempted: boolean;
  tool_call_count: number;
  model_call_count: number;
  output_gate: string;
  suppressed_content: string | null;
  total_cost_usd: number | null;
  latency_ms: number | null;
  prompt_version: string | null;
  model: string | null;
  served_model: string | null;
  tool_trace: ToolTraceEntry[];
  citations: Citation[];
  telemetry_paths: string[];
}

interface Turn {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  meta: TurnMeta | null;
  feedback: {
    rating: 'up' | 'down';
    reason_code: string | null;
    comment: string | null;
    created_at: string;
  } | null;
  tags: Array<{ tag: string; added_by: string | null; created_at: string }>;
  eval_cases: Array<{ id: string; case_type: string; created_at: string }>;
}

interface Transcript {
  conversation: {
    id: string;
    user_id: string;
    boat_id: string | null;
    created_at: string;
    last_message_at: string;
  };
  turns: Turn[];
  refusals: Array<{
    id: string;
    gate: string;
    reason: string;
    query_text: string | null;
    created_at: string;
  }>;
}

const TAGS = ['coverage-gap', 'false-refusal', 'hallucination', 'ux', 'bug'] as const;
const EVAL_TYPES = ['regression', 'golden', 'false_refusal', 'coverage_gap'] as const;

function CitationList({ citations }: { citations: Citation[] }) {
  if (citations.length === 0) return null;
  return (
    <div className="neo-admin-citations">
      <strong>Citations</strong>
      <ul>
        {citations.map((c, i) => (
          <li key={`${c.title}-${i}`}>
            {c.title}
            {c.section !== null ? ` — ${c.section}` : ''}
            {c.page !== null ? ` (p. ${c.page})` : ''}
            {/* Owner-scoped deep link — shown as text, not a link (admins can't open it). */}
            {c.url !== null ? (
              <>
                {' '}
                <code className="admin-muted">{c.url}</code>
              </>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ToolTrace({ trace }: { trace: ToolTraceEntry[] }) {
  if (trace.length === 0) return null;
  return (
    <details className="admin-usage-snapshot">
      <summary>
        {trace.length} tool call{trace.length === 1 ? '' : 's'}
      </summary>
      {trace.map((t) => (
        <div key={t.seq} className="neo-admin-tool">
          <code>
            {t.tool}
            {t.isError ? ' · error' : ''}
          </code>
          <pre>
            args: {t.args}
            {'\n'}→ {t.resultSummary}
          </pre>
        </div>
      ))}
    </details>
  );
}

function ProvenanceLine({ meta }: { meta: TurnMeta }) {
  return (
    <div className="neo-admin-provenance">
      <span>{meta.served_model ?? meta.model ?? 'model ?'}</span>
      <span>{formatCost(meta.total_cost_usd)}</span>
      <span>{formatLatency(meta.latency_ms)}</span>
      {meta.tool_call_count > 0 ? <span>{formatInt(meta.tool_call_count)} tools</span> : null}
      {meta.prompt_version !== null ? <span>{meta.prompt_version}</span> : null}
      {meta.truncated ? <StatusBadge status="suggested" label="truncated" /> : null}
      {meta.manual_attempted ? (
        meta.grounded ? (
          <StatusBadge status="published" label="grounded" />
        ) : (
          <StatusBadge status="suggested" label="ungrounded" />
        )
      ) : null}
      {meta.output_gate !== 'ok' ? (
        <StatusBadge status="rejected" label={`suppressed: ${meta.output_gate}`} />
      ) : null}
      {meta.errored ? <StatusBadge status="failed" label="tool error" /> : null}
    </div>
  );
}

function ReviewActions({
  defaultPrompt,
  onTag,
  onEval,
}: {
  defaultPrompt: string;
  onTag: (tag: string) => void;
  onEval: (caseType: string, userPrompt: string) => void;
}) {
  const [evalType, setEvalType] = useState<string>('regression');
  const [editing, setEditing] = useState(false);
  // Reviewer-authored: pre-filled from the turn they are viewing, but they must
  // confirm/sanitize it — the eval corpus survives account deletion, so it must
  // not hold raw user PII (adversarial-review finding).
  const [prompt, setPrompt] = useState(defaultPrompt);

  return (
    <div className="neo-admin-actions">
      <span className="admin-muted">Tag:</span>
      {TAGS.map((t) => (
        <button key={t} type="button" className="admin-link-button" onClick={() => onTag(t)}>
          {t}
        </button>
      ))}
      <span className="neo-admin-actions-sep" />
      {editing ? (
        <div className="neo-admin-eval-form">
          <label className="admin-muted" htmlFor="neo-eval-prompt">
            Eval prompt (edit to remove any personal details — this corpus outlives the user):
          </label>
          <textarea
            id="neo-eval-prompt"
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={3}
            maxLength={4000}
          />
          <div className="neo-admin-actions">
            <select
              value={evalType}
              onChange={(e) => setEvalType(e.target.value)}
              aria-label="Eval case type"
            >
              {EVAL_TYPES.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="admin-link-button"
              disabled={prompt.trim().length === 0}
              onClick={() => {
                onEval(evalType, prompt.trim());
                setEditing(false);
              }}
            >
              Save eval case
            </button>
            <button type="button" className="admin-link-button" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button type="button" className="admin-link-button" onClick={() => setEditing(true)}>
          Save as eval case…
        </button>
      )}
    </div>
  );
}

export function AdminNeoSessionDetail() {
  const { id } = useParams<{ id: string }>();
  const [search] = useSearchParams();
  const targetTurn = search.get('turn');
  const [reason, setReason] = useState('');
  const [data, setData] = useState<Transcript | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [revealPii, setRevealPii] = useState(false);

  // Once the transcript is open, bring the linked turn into view.
  useEffect(() => {
    if (data === null || targetTurn === null) return;
    document
      .getElementById(`neo-turn-${targetTurn}`)
      ?.scrollIntoView({ behavior: 'instant', block: 'center' });
  }, [data, targetTurn]);

  async function open(e: FormEvent) {
    e.preventDefault();
    if (id === undefined || reason.trim().length < 3) return;
    setLoading(true);
    setError(null);
    try {
      const res = await adminApi<Transcript>(
        `/v1/admin/neo/sessions/${id}?reason=${encodeURIComponent(reason.trim())}`
      );
      setData(res);
    } catch (err) {
      const e2 = err as { status?: number; body?: { message?: string } };
      setError(
        e2.status === 404
          ? 'Conversation not found.'
          : (e2.body?.message ?? 'Failed to load transcript.')
      );
    } finally {
      setLoading(false);
    }
  }

  async function tagTurn(messageId: string, tag: string) {
    try {
      await adminApi(`/v1/admin/neo/messages/${messageId}/tag`, { method: 'POST', body: { tag } });
      setData((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              turns: prev.turns.map((t) =>
                t.id === messageId
                  ? {
                      ...t,
                      tags: [
                        ...t.tags,
                        { tag, added_by: null, created_at: new Date().toISOString() },
                      ],
                    }
                  : t
              ),
            }
      );
    } catch {
      setError('Failed to tag turn.');
    }
  }

  async function evalTurn(messageId: string, caseType: string, userPrompt: string) {
    try {
      const res = await adminApi<{ id: string }>(`/v1/admin/neo/messages/${messageId}/eval`, {
        method: 'POST',
        body: { caseType, userPrompt },
      });
      setData((prev) =>
        prev === null
          ? prev
          : {
              ...prev,
              turns: prev.turns.map((t) =>
                t.id === messageId
                  ? {
                      ...t,
                      eval_cases: [
                        ...t.eval_cases,
                        { id: res.id, case_type: caseType, created_at: new Date().toISOString() },
                      ],
                    }
                  : t
              ),
            }
      );
    } catch {
      setError('Failed to save eval case.');
    }
  }

  return (
    <section className="admin-section">
      <p>
        <Link to="/admin/neo">← Back to Neo chats</Link>
      </p>

      {data === null ? (
        <>
          <header className="admin-section-header">
            <h1>Open transcript</h1>
            <GuideLink section="fix-mark" />
          </header>
          {targetTurn !== null ? (
            <p className="admin-hint">
              The transcript opens at the turn you followed from the eval case inbox.
            </p>
          ) : null}
          <div className="admin-auth-banner">
            You are about to view a user&apos;s private conversation. This access is logged with
            your account and your stated reason.
          </div>
          <form className="neo-admin-reason" onSubmit={(e) => void open(e)}>
            <label htmlFor="neo-reason">Reason for viewing</label>
            <input
              id="neo-reason"
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. investigating a thumbs-down on grounding"
              minLength={3}
              maxLength={200}
              required
            />
            <button
              type="submit"
              className="admin-button"
              disabled={loading || reason.trim().length < 3}
            >
              {loading ? 'Opening…' : 'Open transcript'}
            </button>
          </form>
          {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
        </>
      ) : (
        <>
          <header className="admin-section-header">
            <h1>Neo transcript</h1>
            <span className="admin-count">
              <button
                type="button"
                className="admin-link-button"
                onClick={() => setRevealPii((v) => !v)}
              >
                {revealPii ? 'Mask PII' : 'Reveal PII'}
              </button>
            </span>
            <GuideLink section="fix-mark" />
          </header>
          <div className="admin-auth-banner">
            Viewing <Link to={`/admin/users/${data.conversation.user_id}`}>this user&apos;s</Link>{' '}
            private conversation — access logged.
          </div>
          <dl className="admin-defs">
            <dt>User</dt>
            <dd>
              <Link to={`/admin/users/${data.conversation.user_id}`}>
                <code>{data.conversation.user_id}</code>
              </Link>
            </dd>
            <dt>Boat</dt>
            <dd>
              {data.conversation.boat_id !== null ? <code>{data.conversation.boat_id}</code> : '—'}
            </dd>
            <dt>Started</dt>
            <dd>{formatDate(data.conversation.created_at)}</dd>
          </dl>

          <div className="neo-admin-transcript">
            {data.turns.map((turn, i) => (
              <div
                key={turn.id}
                id={`neo-turn-${turn.id}`}
                className={`neo-admin-turn neo-admin-turn--${turn.role}${
                  turn.id === targetTurn ? ' neo-admin-turn--target' : ''
                }`}
              >
                <div className="neo-admin-turn-head">
                  <strong>{turn.role === 'assistant' ? 'Neo' : 'User'}</strong>
                  <span className="admin-muted">{formatDate(turn.created_at)}</span>
                </div>
                <div
                  className={
                    revealPii ? 'neo-admin-turn-body' : 'neo-admin-turn-body neo-admin-masked'
                  }
                >
                  {turn.content}
                </div>
                {turn.meta !== null ? (
                  <>
                    <ProvenanceLine meta={turn.meta} />
                    <CitationList citations={turn.meta.citations} />
                    <ToolTrace trace={turn.meta.tool_trace} />
                    {turn.meta.output_gate !== 'ok' && turn.meta.suppressed_content !== null ? (
                      <details className="admin-usage-snapshot">
                        <summary>Suppressed model output ({turn.meta.output_gate})</summary>
                        <pre className={revealPii ? '' : 'neo-admin-masked'}>
                          {turn.meta.suppressed_content}
                        </pre>
                      </details>
                    ) : null}
                  </>
                ) : null}
                {turn.feedback !== null ? (
                  <OwnerFeedback feedback={turn.feedback} revealPii={revealPii} />
                ) : null}
                {turn.tags.length > 0 ? (
                  <div className="neo-admin-taglist">
                    {turn.tags.map((t, i) => (
                      <StatusBadge key={`${t.tag}-${i}`} status="suggested" label={t.tag} />
                    ))}
                  </div>
                ) : null}
                {turn.eval_cases.length > 0 ? (
                  <div className="neo-admin-taglist">
                    {turn.eval_cases.map((ev) => (
                      <StatusBadge key={ev.id} status="published" label={`eval: ${ev.case_type}`} />
                    ))}
                  </div>
                ) : null}
                {turn.role === 'assistant' ? (
                  <ReviewActions
                    defaultPrompt={
                      i > 0 && data.turns[i - 1]!.role === 'user' ? data.turns[i - 1]!.content : ''
                    }
                    onTag={(tag) => void tagTurn(turn.id, tag)}
                    onEval={(caseType, userPrompt) => void evalTurn(turn.id, caseType, userPrompt)}
                  />
                ) : null}
              </div>
            ))}
          </div>

          {data.refusals.length > 0 ? (
            <>
              <h2>Refusals in this session</h2>
              <div className="scroll-x">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Gate</th>
                      <th>Reason</th>
                      <th>What the user asked</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.refusals.map((r) => (
                      <tr key={r.id}>
                        <td>{formatDate(r.created_at)}</td>
                        <td>{r.gate}</td>
                        <td>
                          <StatusBadge status="rejected" label={r.reason} />
                        </td>
                        <td className={revealPii ? '' : 'neo-admin-masked'}>
                          {r.query_text ?? '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : null}
          {error !== null ? <div className="admin-auth-banner error">{error}</div> : null}
        </>
      )}
    </section>
  );
}
